/* eslint-disable no-restricted-syntax */
/* eslint-disable ts/consistent-type-definitions */
import type { CSSProperties } from "vue";
import type { ColorProp, StyleProp } from "../bridge/selection";
import { AxLetterCasing } from "../bridge/selection";
import { toInlineArgb } from "../common/utils";

export const PT_TO_PX = 4 / 3;
export const PX_TO_PT = 3 / 4;

export type Version = {
  Major: number;
  Minor: number;
  Build: number;
  Revision: number;
};

export const enum AxFontStyle {
  None = -1,
  Normal = 0,
  Italic = 1,
  Oblique = 2,
}

export const enum Baseline {
  Normal = 0,
  Superscript = 1,
  Subscript = 2,
}

export const enum AxWordWrapping {
  Wrap = 0,
  NoWrap = 1,
  /// <summary>
  ///    Note??Windows?8.1 and later only. ? Words are broken across lines to avoid text
  //     overflowing the layout box. Emergency wrapping occurs if the word is larger than
  //     the maximum width.
  /// </summary>
  EmergencyBreak = 2,
  WholeWord = 3,
  Character = 4,
}

export const enum AxStringTrimming {
  /// <summary>
  /// Clipping: NO
  /// WordWrap: YES
  /// Ellipsis: NO
  /// </summary>
  None = 0,
  // Character = 1,
  /// <summary>
  /// Clipping: YES
  /// WordWrap: YES
  /// Ellipsis: NO
  /// </summary>
  Word = 2,
  /// <summary>
  /// Clipping: YES
  /// WordWrap: NO
  /// Ellipsis: YES
  /// </summary>
  EllipsisCharacter = 3,
  // EllipsisWord = 4,
  // EllipsisPath = 5
}

export const enum TextListType {
  Bullet = 0,
  Numbered = 1,
  Task = 2,
  // could add more here including a "custom"
}

export const enum FontStretch {
  UndefinedStretch = 0,
  UltraCondensed = 1,
  ExtraCondensed = 2,
  Condensed = 3,
  SemiCondensed = 4,
  Normal = 5,
  SemiExpanded = 6,
  Expanded = 7,
  ExtraExpanded = 8,
  UltraExpanded = 9,

  // eslint-disable-next-line ts/prefer-literal-enum-member
  Medium = Normal,
}

export const enum AxStringAlignment {
  Near = 0,
  Center = 1,
  Far = 2,
  Justify = 3,
  // just for help with menus right now
  None = -1,
}

export type TextListInfo = {
  IndentLevel: number;
  ListType: TextListType;
  Checked?: boolean;
  Start?: number;
};

export const enum InlineType {
  AxRun = "Axure:AttributedString:AxRun",
  AxHyperlink = "Axure:AttributedString:AxHyperlink",
}

export type AxRun = {
  Type: InlineType.AxRun;
  Text: string;
  Family: StyleProp<string>;
  Size: StyleProp<number>;
  Weight: number;
  Style: AxFontStyle;
  Stretch: FontStretch;
  Underline: boolean;
  Color: ColorProp;
  Baseline: Baseline;
  CharacterSpacing: StyleProp<number>;
  Strikethrough: boolean;
  LetterCasing: AxLetterCasing;
};

export const enum AxHyperlinkPayloadType {
  Mark = 0,
  Mention = 1,
  InlineCard = 2,
  CanvasLink = 3,
}

export type AxHyperlinkMark = {
  type: AxHyperlinkPayloadType.Mark;
  href: string;
};

export type AxHyperlinkMention = {
  type: AxHyperlinkPayloadType.Mention;
  id: string;
  mentionId: string;
};

export type AxHyperlinkInlineCard = {
  type: AxHyperlinkPayloadType.InlineCard;
  href: string;
};

export type AxHyperlinkCanvasLink = {
  type: AxHyperlinkPayloadType.CanvasLink;
  objectId: string;
};

export type AxHyperlinkPayload
  = AxHyperlinkMark
    | AxHyperlinkMention
    | AxHyperlinkInlineCard
    | AxHyperlinkCanvasLink
;

// NOTE: The actual structure on the C# side has an AxSpan which (in practice) only AxHyperlink derives from. If we wanted
// other content types (like @mentions or something like that) and we didn't want them to be ProseMirror Link objects, we will
// want to carry that over and we might even allow spans within spans. Right now RP doesn't support that and doesn't need that
// so i have simplified that for the c# side.
export type AxHyperlink = {
  Type: InlineType.AxHyperlink;
  Inlines: AxRun[];
  Link: AxHyperlinkPayload;
};

export type AxInline = AxRun | AxHyperlink;

export type AxParagraph = {
  // Wrapping: number,
  // Trimming: number, i don't think we need trimming or wrapping.
  HorizontalAlignment: AxStringAlignment;
  LineSpacing: number;
  ListInfo: TextListInfo;
  Inlines: AxInline[];
};

// TODO: Add default style (AxTextStyle) to AxAttributedString?
export type AxAttributedString = {
  Version: Version;
  Paragraphs: AxParagraph[];
};

export type AxFullTextStyle = {
  Family: StyleProp<string>;
  Size: StyleProp<number>;
  Weight: number;
  Style: AxFontStyle;
  Stretch: FontStretch;
  Underline: boolean;
  Color: ColorProp;
  HorizontalAlignment: AxStringAlignment;
  LineSpacing: number;
  CharacterSpacing: StyleProp<number>;
  Strikethrough: boolean;
  ListInfo: TextListInfo;
  Baseline: Baseline;
  LetterCasing: AxLetterCasing;
};
export type AxTextStyle = Partial<AxFullTextStyle>;

export const CURRENT_FILE_VERSION: Version = {
  Major: 10,
  Minor: 0,
  Build: 4,
  Revision: 0,
};

export const DEFAULT_TEXT_STYLE: AxFullTextStyle = {
  Family: { value: "Inter", indeterminate: false },
  Size: { value: 9.75, indeterminate: false },
  Weight: 400,
  Style: AxFontStyle.Normal,
  Stretch: FontStretch.Normal,
  Underline: false,
  Color: { value: -13421773 },
  HorizontalAlignment: AxStringAlignment.Near,
  LineSpacing: 0,
  CharacterSpacing: { value: 0, indeterminate: false },
  Strikethrough: false,
  ListInfo: { IndentLevel: 0, ListType: 0 },
  Baseline: Baseline.Normal,
  LetterCasing: AxLetterCasing.Normal,
};

function getStyleProp<T extends keyof AxTextStyle>(prop: T, baseStyle: AxTextStyle, overrides?: AxTextStyle) {
  const overVal = overrides && overrides[prop];
  if (overVal !== undefined) return overVal;
  const baseProp = baseStyle[prop];
  if (baseProp !== undefined) return baseProp;
  return undefined;
}

export function getAxInline(baseStyle: AxTextStyle, text?: string, overrides?: AxTextStyle): AxRun {
  return {
    Type: InlineType.AxRun,
    Text: text ?? "\n",
    Family: getStyleProp("Family", baseStyle, overrides)!,
    Size: getStyleProp("Size", baseStyle, overrides)!,
    Weight: getStyleProp("Weight", baseStyle, overrides)!,
    Style: getStyleProp("Style", baseStyle, overrides)!,
    Stretch: getStyleProp("Stretch", baseStyle, overrides)!,
    Underline: getStyleProp("Underline", baseStyle, overrides)!,
    Color: getStyleProp("Color", baseStyle, overrides)!,
    Baseline: getStyleProp("Baseline", baseStyle, overrides)!,
    CharacterSpacing: getStyleProp("CharacterSpacing", baseStyle, overrides)!,
    Strikethrough: getStyleProp("Strikethrough", baseStyle, overrides)!,
    LetterCasing: getStyleProp("LetterCasing", baseStyle, overrides)!,
  };
}

export function getBaseAxInline() {
  return getAxInline(DEFAULT_TEXT_STYLE);
}

export function getBaseAxParagraph(): AxParagraph {
  return {
    // Wrapping: 0,
    // Trimming: 0,
    HorizontalAlignment: DEFAULT_TEXT_STYLE.HorizontalAlignment ?? AxStringAlignment.Near,
    LineSpacing: DEFAULT_TEXT_STYLE.LineSpacing ?? 0,
    ListInfo: DEFAULT_TEXT_STYLE.ListInfo ?? { IndentLevel: 0, ListType: 0 },
    Inlines: [],
  };
}

export function getCssTextAlignment(align: AxStringAlignment) {
  // do we need to handl RTL here?
  // we don't support justified for now.
  // if (align === AxStringAlignment.Near) return "left";
  if (align === AxStringAlignment.Center) return "center";
  if (align === AxStringAlignment.Far) return "right";
  return "left";
}

// the par props (line spacing and horizontal alignment) are special because pm will automatically return
// the default value so we want "inherit to also return undefined"
export function getStringAlignment(cssVal: string | undefined) {
  if (cssVal === undefined || cssVal === "inherit") return undefined;
  if (cssVal === "center") return AxStringAlignment.Center;
  if (cssVal === "right") return AxStringAlignment.Far;
  return AxStringAlignment.Near;
}

export function getCssVerticalAlignment(align: AxStringAlignment) {
  if (align === AxStringAlignment.Center) return "center";
  if (align === AxStringAlignment.Far) return "end";
  return "start";
}

export function getCssLineHeight(lineSpacing: number) {
  // return lineSpacing === 0 ? "normal" : `${lineSpacing}px`;
  return `${lineSpacing}`;
}

// the par props (line spacing and horizontal alignment) are special because pm will automatically return
// the default value so we want "inherit to also return undefined"
export function getLineSpacingFromCss(cssLineHeight: string | undefined) {
  if (cssLineHeight === undefined || cssLineHeight === "inherit") return undefined;
  return cssLineHeight ? Number(cssLineHeight.replace("%", "")) : 1.0;
}

export function getPropsFromDecoration(textDecoration: string) {
  const hasUnderline = textDecoration.includes("underline");
  const hasStrike = textDecoration.includes("line-through");
  return { underline: hasUnderline, strikethrough: hasStrike };
}

export function getCssFontSize(pointNumber: number) {
  if (pointNumber === 0) return "normal";
  return `${pointNumber * PT_TO_PX}px`;
}

export function getFontSizeFromCss(spacing: string): number;
export function getFontSizeFromCss(spacing?: string): number | undefined;
export function getFontSizeFromCss(spacing?: string) {
  if (spacing === undefined || spacing === null) return undefined;
  if (spacing === "normal") return 0;
  return Number(spacing.replace("px", "")) * PX_TO_PT;
}

export function getCssPixelNumber(val?: number) {
  if (val === undefined) return undefined;
  return `${val}px`;
}
export function getNumberFromCss(val: string): number;
export function getNumberFromCss(val?: string): number | undefined;
export function getNumberFromCss(val?: string) {
  if (val == null) return undefined;
  return Number(val.replace("px", ""));
}

export function getCssValign(b: Baseline) {
  if (b === Baseline.Superscript) return "super";
  if (b === Baseline.Subscript) return "sub";
  return "baseline";
}
export function getBaselineFromCss(vAlign: string): Baseline;
export function getBaselineFromCss(vAlign?: string) {
  if (vAlign === undefined) return undefined;
  if (vAlign === "super") return Baseline.Superscript;
  if (vAlign === "sub") return Baseline.Subscript;
  return Baseline.Normal;
}

export function getCssFontWeight(val: number) {
  return `${val}`;
}
export function getFontWeightFromCss(val: string): number;
export function getFontWeightFromCss(val?: string): number | undefined;
export function getFontWeightFromCss(val?: string) {
  if (val === undefined) return undefined;
  if (val === "normal") return 400;
  if (val === "bold") return 700;
  return Number(val);
}

export function getCssFontStretch(val: number) {
  switch (val) {
    case FontStretch.UltraCondensed:
      return "ultra-condensed";
    case FontStretch.ExtraCondensed:
      return "extra-condensed";
    case FontStretch.Condensed:
      return "condensed";
    case FontStretch.SemiCondensed:
      return "semi-condensed";
    case FontStretch.SemiExpanded:
      return "semi-expanded";
    case FontStretch.Expanded:
      return "expanded";
    case FontStretch.ExtraExpanded:
      return "extra-expanded";
    case FontStretch.UltraExpanded:
      return "ultra-expanded";
    case FontStretch.UndefinedStretch:
    case FontStretch.Normal:
    default:
      return "normal";
  }
}

export function getFontStretchFromCss(stretch?: string) {
  if (stretch === undefined) return undefined;
  switch (stretch.trim().toLowerCase()) {
    case "ultra-condensed":
      return FontStretch.UltraCondensed;
    case "extra-condensed":
      return FontStretch.ExtraCondensed;
    case "condensed":
      return FontStretch.Condensed;
    case "semi-condensed":
      return FontStretch.SemiCondensed;
    case "semi-expanded":
      return FontStretch.SemiExpanded;
    case "expanded":
      return FontStretch.Expanded;
    case "extra-expanded":
      return FontStretch.ExtraExpanded;
    case "ultra-expanded":
      return FontStretch.UltraExpanded;
    case "normal":
    case "medium":
      return FontStretch.Normal;
    default:
      return FontStretch.UndefinedStretch;
  }
}

export function getCssFontStyle(val: AxFontStyle) {
  if (val === AxFontStyle.Italic) return "italic";
  if (val === AxFontStyle.Oblique) return "oblique";
  return "normal";
}

export function getFontStyleFromCss(style?: string) {
  if (style === undefined) return undefined;
  if (style === "italic") return AxFontStyle.Italic;
  if (style === "oblique") return AxFontStyle.Oblique;
  return AxFontStyle.Normal;
}

// Get the 'text-decoration' css value
export function getTextDecoration(underline: boolean, strikethrough: boolean) {
  if (underline && strikethrough) return "underline line-through";
  if (underline) return "underline";
  if (strikethrough) return "line-through";
  return "";
}

export function getListInfoFromBulletList(bulletList: boolean): TextListInfo {
  // need to fix this for nesting
  return bulletList ? { IndentLevel: 1, ListType: TextListType.Bullet } : { IndentLevel: 0, ListType: TextListType.Bullet };
}

export function getCssLetterCasing(val: AxLetterCasing) {
  if (val === AxLetterCasing.Uppercase) return "uppercase";
  if (val === AxLetterCasing.Lowercase) return "lowercase";
  return "none";
}

export function getLetterCasingFromCss(style?: string) {
  // if (style === undefined) return undefined;
  if (style === "uppercase") return AxLetterCasing.Uppercase;
  if (style === "lowercase") return AxLetterCasing.Lowercase;
  return AxLetterCasing.Normal;
}

/// Set css rules for text editor element (base text editor styles)
export function setCssFromTextStyle(element: HTMLElement, style: AxTextStyle) {
  // const div = this.getEditorElement(id) as HTMLDivElement;

  // div.style.textAlign = getCssTextAlignment(firstPar.HorizontalAlignment);
  // div.style.lineHeight = getCssPixelNumber(firstPar.LineSpacing);

  if (style.Family) element.style.fontFamily = style.Family.value;
  if (style.Size) element.style.fontSize = getCssFontSize(style.Size.value);
  if (style.Weight) element.style.fontWeight = style.Weight.toString();
  if (style.Style) element.style.fontStyle = style.Style.toString();
  if (style.Stretch) element.style.fontStretch = getCssFontStretch(style.Stretch);
  if (style.Strikethrough ?? style.Underline) element.style.textDecoration = getTextDecoration(style.Underline ?? false, style.Strikethrough ?? false);
  if (style.Color) element.style.color = toInlineArgb(style.Color.value);
  if (style.Baseline) element.style.verticalAlign = getCssValign(style.Baseline);

  if (style.CharacterSpacing) element.style.letterSpacing = `${style.CharacterSpacing.value * PT_TO_PX}px`;
}

/// <summary>
///
/// </summary>
export function getAxStyleFromRun(run: AxRun, baseStyle?: AxTextStyle) {
  const b = baseStyle ?? {};
  const result = {} as AxTextStyle;

  if (b.Family !== run.Family) result.Family = run.Family;
  if (b.Size !== run.Size) result.Size = run.Size;
  if (b.Weight !== run.Weight) result.Weight = run.Weight;
  if (b.Style !== run.Style) result.Style = run.Style;
  if (b.Stretch !== run.Stretch) result.Stretch = run.Stretch;
  if (b.Strikethrough !== run.Strikethrough) result.Strikethrough = run.Strikethrough;
  if (b.Underline !== run.Underline) result.Underline = run.Underline;
  if (b.Color !== run.Color) result.Color = run.Color;
  if (b.Baseline !== run.Baseline) result.Baseline = run.Baseline;
  if (b.CharacterSpacing !== run.CharacterSpacing) result.CharacterSpacing = run.CharacterSpacing;

  return result;
}

function getFirstRun(inlines: AxInline[]): AxRun | undefined {
  const inline = inlines[0];
  if (!inline) return undefined;
  return inline.Type === InlineType.AxRun ? inline : getFirstRun(inline.Inlines);
}

/// <summary>
///
/// </summary>
export function getAxStyleBaseStyle(attrStr: AxAttributedString): AxFullTextStyle {
  // const result = {} as AxFullTextStyle;

  const firstPar = attrStr.Paragraphs[0];
  if (!firstPar) throw new Error("AttributedString should have at least one Paragraph.");
  const run = getFirstRun(firstPar.Inlines);
  if (!run) throw new Error("AttributedString Paragraph should have at least one Run.");

  // result.HorizontalAlignment = firstPar.HorizontalAlignment;
  // result.LineSpacing = firstPar.LineSpacing;

  return {
    Family: run.Family,
    Size: run.Size,
    Weight: run.Weight,
    Style: run.Style,
    Stretch: run.Stretch,
    Strikethrough: run.Strikethrough,
    Underline: run.Underline,
    Color: run.Color,
    Baseline: run.Baseline,
    CharacterSpacing: run.CharacterSpacing,
    LetterCasing: run.LetterCasing,
    HorizontalAlignment: firstPar.HorizontalAlignment,
    LineSpacing: firstPar.LineSpacing,
    ListInfo: firstPar.ListInfo,
  };

  // return result;
}

export function getCssForTextStyle(s: AxTextStyle): CSSProperties {
  const val = {} as CSSProperties;

  if (s.Family) val.fontFamily = s.Family.value;
  if (s.Size) val.fontSize = getCssFontSize(s.Size.value);
  if (s.Weight) val.fontWeight = s.Weight;
  if (s.Style) val.fontStyle = getCssFontStyle(s.Style);
  if (s.Stretch) val.fontStretch = getCssFontStretch(s.Stretch);
  // Underline: boolean,
  // fix underline later
  if (s.Color) val.color = toInlineArgb(s.Color.value);
  if (s.HorizontalAlignment) val.textAlign = getCssTextAlignment(s.HorizontalAlignment);
  if (s.LineSpacing) val.lineHeight = getCssLineHeight(s.LineSpacing);
  if (s.CharacterSpacing) val.letterSpacing = s.CharacterSpacing.value;
  // Strikethrough: boolean,
  // fix strikethrough later
  // ListInfo: TextListInfo,
  // fix list info later
  // Baseline: Baseline,
  // fix baseline later.

  return val;
}

export function getEmptyAttributedString(): AxAttributedString {
  const par = getBaseAxParagraph();
  par.Inlines.push({ ...getBaseAxInline(), Text: "" });
  return {
    Version: CURRENT_FILE_VERSION,
    Paragraphs: [par],
  };
}

function getInlineText(parOrSpan: AxParagraph | AxHyperlink): string {
  const inlines = parOrSpan.Inlines;
  return inlines.map(inline => (inline.Type === InlineType.AxRun ? inline.Text : getInlineText(inline))).join();
}

function getParText(par: AxParagraph) {
  return getInlineText(par);
}

export function getText(attrStr: AxAttributedString) {
  return attrStr.Paragraphs.map(getParText).join("\n");
}
